import React, { useEffect, useMemo, useState } from "react";
import apiService from "../services/apiService";
import I from "../component/Icon";
import SB from "../component/SearchBox";
const PAGE_SIZE = 10;
const get = (obj, ...names) => names.map(k => obj?.[k]).find(v => v !== undefined && v !== null);
const active = v => v !== false && v !== 0 && v !== "0" && String(v).toLowerCase() !== "false";
const listFrom = (value) => {
  if (Array.isArray(value)) return value;
  value = parseJson(value);
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  for (const key of ["result", "Result", "rows", "Rows", "items", "Items", "studentDetails", "StudentDetails", "output", "Output", "data", "Data"]) {
    if (value[key] !== undefined) { const rows = listFrom(value[key]); if (rows.length) return rows; }
  }
  return [];
};
const parseJson = value => {
  if (typeof value !== "string") return value;
  try { return JSON.parse(value); } catch { return value; }
};
const unwrap = data => parseJson(get(parseJson(data), "output", "Output", "data", "Data") ?? data);
const unwrapStudent = data => {
  let value = parseJson(data);
  for (let i = 0; i < 8; i++) {
    if (Array.isArray(value)) return value[0] || null;
    if (!value || typeof value !== "object") return null;
    const next = get(value, "output", "Output", "data", "Data", "result", "Result");
    if (next == null) return value;
    value = parseJson(next);
  }
  return value;
};
const checkResponse = response => {
  const data = response?.data ?? response;
  if (get(data, "isSuccess", "IsSuccess") === false) throw new Error(get(data, "failureReason", "FailureReason", "message", "Message") || "Request failed");
  return data;
};
const asId = v => Number(v) || 0;
const studentIdOf = x => asId(get(x, "studentId", "StudentId", "id", "Id"));
const studentNameOf = x => {
  const name = get(x, "studentName", "StudentName", "fullName", "FullName", "name", "Name");
  return name || [get(x, "firstName", "FirstName"), get(x, "middleName", "MiddleName"), get(x, "lastName", "LastName")].filter(Boolean).join(" ") || `Student #${studentIdOf(x)}`;
};
const detailsOf = student => {
  // Book dispatch rows come only from the StudentBookSends endpoint.
  const raw = get(student, "bookSends", "BookSends");
  if (raw == null) return [];
  const parsed = parseJson(raw);
  if (Array.isArray(parsed)) return parsed;
  if (parsed && typeof parsed === "object") {
    return listFrom(parsed);
  }
  return [];
};
const detailIdOf = d => asId(get(d, "id", "Id", "studentBookSendId", "StudentBookSendId"));
const examIdOf = d => asId(get(d, "examId", "ExamId"));
const bookIdOf = d => asId(get(d, "bookDetailId", "BookDetailId"));
const trackOf = d => String(get(d, "postTrackNo", "PostTrackNo") ?? "");
const postTrackDateOf = d => get(d, "postTrackDate", "PostTrackDate") ?? null;
// Input type=date requires YYYY-MM-DD; preserve the calendar date from API.
const dateInputValue = value => value ? String(value).slice(0, 10) : "";
const sentOf = d => { const v = get(d, "isBookSend", "IsBookSend"); return v === true || v === 1 || v === "1" || String(v).toLowerCase() === "true"; };
const dateText = v => { if (!v) return "—"; const d = new Date(v); return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN", {day:"2-digit",month:"short",year:"numeric"}); };
export default function StudentBookAssign({
  title = "Student Book Assignment",
  listUrl = "StudentBookSend/getPaginatedStudentBookSends",
  studentByIdUrl = id => `Student/getStudentById/${id}`,
  bookMasterUrl = "BookMaster/getPaginatedBookMasters",
  examUrl = "Exam/getAllExam",
  updateBookStatusUrl = "StudentBookSend/updateStudentBookSend",
  deleteAssignmentUrl = "StudentBookSend/DeleteStudentBookSend",
  addAssignmentUrl = "StudentBookSend/addStudentBookSend",
  onUpdateBookStatus,        // Optional: async ({ studentId, studentDetailId, bookId, isBookSend }) => ...
  onDeleteAssignment,        // Optional: async ({ studentId, studentDetailId, bookId }) => ...
  onAddAssignment,           // Optional: async ({ studentId, examId, bookId }) => ...
  onBack,
}) {
  const [students, setStudents] = useState([]); // Rows are grouped from StudentBookSends, not StudentDetails
  const [books, setBooks] = useState([]);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [notice, setNotice] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [examFilter, setExamFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState(null);
  const [draft, setDraft] = useState({});

  // Same logged-in identity stored by LoginPage/App.js.
  // A student must never fall through to the all-students list.
  const loginUser = parseJson(sessionStorage.getItem("user") || "{}") || {};
  const loggedStudentId = asId(
    sessionStorage.getItem("studentId") ?? get(loginUser, "studentId", "StudentId")
  );
  const roleLabel = String(
    get(loginUser, "roleName", "RoleName") ?? sessionStorage.getItem("roleName") ?? ""
  ).trim().toLowerCase().replace(/[\s_-]+/g, "");
  const isStudentLogin = loggedStudentId > 0 || roleLabel === "student";

  // BookMaster-style top-right message, automatically dismissed after 5 seconds.
  const notify = (type, text) => { setNotice({ type, text, id: Date.now() }); };
  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(null), 5000);
    return () => window.clearTimeout(timer);
  }, [notice]);
  const refresh = async () => {
    setLoading(true); setLoadError("");
    try {
      // Source of truth: one dispatch record per BookDetailId.
      const all = [];
      const pageSize = 100;
      for (let pageNumber = 1; pageNumber <= 200; pageNumber++) {
        const body = {
          Page: pageNumber, PageSize: pageSize, SearchTerm: "",
          SortDirection: "DESC", SortField: "Id",
          IncludeInActive: false, IsActive: true,
        };
        const response = checkResponse(await apiService.post(listUrl, body));
        const output = unwrap(response);
        const rows = listFrom(output);
        if (!Array.isArray(rows)) throw new Error("Invalid StudentBookSend pagination response");
        all.push(...rows);
        const count = Number(get(output, "rowCount", "RowCount", "totalRecords", "TotalRecords", "totalCount", "TotalCount"));
        if (rows.length < pageSize || (Number.isFinite(count) && count > 0 && all.length >= count)) break;
        if (pageNumber === 200) throw new Error("StudentBookSend pagination exceeded 200 pages");
      }
      const valid = [...new Map(all.filter(row =>
        detailIdOf(row) > 0 && studentIdOf(row) > 0 && bookIdOf(row) > 0 &&
        active(get(row, "isActive", "IsActive")) &&
        (!isStudentLogin || studentIdOf(row) === loggedStudentId)
      ).map(row => [detailIdOf(row), row])).values()];

      const grouped = new Map();
      valid.forEach(row => {
        const sid = studentIdOf(row);
        if (!grouped.has(sid)) grouped.set(sid, []);
        grouped.get(sid).push(row);
      });
      const result = await Promise.all([...grouped].map(async ([sid, bookSends]) => {
        const nameOnBookSend = studentNameOf(bookSends[0]);
        let studentName = nameOnBookSend;
        // Name lookup only; never take book/dispatch status from StudentDetails.
        if (nameOnBookSend === `Student #${sid}`) {
          try {
            const url = typeof studentByIdUrl === "function" ? studentByIdUrl(sid) : `${studentByIdUrl}/${sid}`;
            const student = unwrapStudent(checkResponse(await apiService.get(url)));
            if (student) studentName = studentNameOf(student);
          } catch (err) { console.warn(`Student ${sid} name lookup failed`, err); }
        }
        return { id: sid, studentId: sid, studentName, bookSends };
      }));
      setStudents(result);
    } catch (error) {
      setLoadError(error?.message || "StudentBookSend list load failed.");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [listUrl]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const foundBooks = []; let localExams = [];
      try {
        for (let pg=1; pg<=30; pg++) {
          const body = { Page:pg, PageSize:100, SearchTerm:"", SortDirection:"DESC", SortField:"Id", IncludeInActive:false, IsActive:true };
          const response = checkResponse(await apiService.post(bookMasterUrl, body));
          const rows = listFrom(response);
          foundBooks.push(...rows);
          if (rows.length < 100) break;
        }
      } catch (err) { if (!cancelled) notify("error", `Book Master: ${err.message}`); }
      try { localExams = listFrom(checkResponse(await apiService.get(examUrl))); } catch { /* IDs still display */ }
      if (!cancelled) { setBooks(foundBooks); setExams(localExams); }
    })();
    return () => { cancelled = true; };
  }, [bookMasterUrl, examUrl]);
  const bookLookup = useMemo(() => {
    const map = new Map();
    books.forEach(master => {
      const name = String(get(master, "competitiveExams", "CompetitiveExams") || "");
      const parts = get(master, "teaBooAssDetlst", "TeaBooAssDetlst", "bookDetails", "BookDetails") || [];
      (Array.isArray(parts) ? parts : []).forEach(b => {
        const id = asId(get(b, "id", "Id"));
        if (id) map.set(id, { id, name: String(get(b, "bookName", "BookName") || `Book #${id}`), examLabel: name, masterId: asId(get(master, "id", "Id")) });
      });
    });
    return map;
  }, [books]);
  const examLookup = useMemo(() => new Map(exams.map(e => [asId(get(e,"id","Id","examId","ExamId")), String(get(e,"examName","ExamName","name","Name") || "")])), [exams]);
  const records = useMemo(() => students.filter(student =>
    !isStudentLogin || studentIdOf(student) === loggedStudentId
  ).map(student => {
    const sid = studentIdOf(student);
    const details = detailsOf(student).filter(d => active(get(d,"isActive","IsActive")) && bookIdOf(d) > 0);
    const done = details.filter(sentOf).length;
    return { student, sid, name: studentNameOf(student), details, done, total: details.length,
      exams: Array.from(new Set(details.map(examIdOf).filter(Boolean))) };
  }).filter(r => r.total), [students, isStudentLogin, loggedStudentId]);
  const filtered = useMemo(() => records.filter(r => {
    const term = search.trim().toLowerCase();
    const matchesText = !term || [r.name, r.sid, ...r.details.map(d => bookLookup.get(bookIdOf(d))?.name || `Book #${bookIdOf(d)}`)].some(t => String(t).toLowerCase().includes(term));
    const matchesStatus = statusFilter === "all" || (statusFilter === "sent" ? r.done === r.total : statusFilter === "pending" ? r.done === 0 : r.done > 0 && r.done < r.total);
    const matchesExam = examFilter === "all" || r.exams.includes(Number(examFilter));
    return matchesText && matchesStatus && matchesExam;
  }), [records,search,statusFilter,examFilter,bookLookup]);
  const maxPage = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page,maxPage);
  const shown = filtered.slice((currentPage-1)*PAGE_SIZE,currentPage*PAGE_SIZE);
  const totalBooks = records.reduce((s,r)=>s+r.total,0);
  const sentBooks = records.reduce((s,r)=>s+r.done,0);
  const detailBooks = r => r.details.map(d => ({ ...d, _detailId:detailIdOf(d), _bookId:bookIdOf(d), _examId:examIdOf(d), _sent:sentOf(d), _name: bookLookup.get(bookIdOf(d))?.name || `Book #${bookIdOf(d)}` }));
  const openView = r => { setDialog({type:"view",record:r}); setDraft({}); };
  const openEdit = r => {
    if (isStudentLogin) return;
    setDialog({ type: "edit", record: r });
    setDraft({
      ...Object.fromEntries(r.details.map(d => [String(detailIdOf(d)), sentOf(d)])),
      ...Object.fromEntries(r.details.map(d => [`track_${detailIdOf(d)}`, trackOf(d)])),
      ...Object.fromEntries(r.details.map(d => [`trackDate_${detailIdOf(d)}`, dateInputValue(postTrackDateOf(d))])),
    });
  };
  const openDelete = (r,d) => { if (isStudentLogin) return; setDialog({type:"delete",record:r,detail:d}); };
  const openAdd = () => { if (isStudentLogin) return; setDraft({studentId:"",examId:"",bookId:""}); setDialog({type:"add"}); };
  // Update/Delete by StudentBookSends.Id. StudentDetails and fees are never modified.
  const persistDelete = async data => {
    if (typeof onDeleteAssignment === "function") return onDeleteAssignment(data);
    // API contract assumption: HTTP DELETE with Id in JSON body.
    // If backend uses route /{id} or query ID, adjust only this call.
    return checkResponse(await apiService.delete(deleteAssignmentUrl, { data: { Id: data.studentBookSendId } }));
  };
  const persistAdd = async data => {
    if (typeof onAddAssignment === "function") return onAddAssignment(data);
    return checkResponse(await apiService.post(addAssignmentUrl, {
      StudentId: data.studentId, ExamId: data.examId, BookDetailId: data.bookId,
      PostTrackNo: "0", PostTrackDate: null, CourierCharges: 0,
      IsBookSend: false, IsActive: true,
    }));
  };
  const saveEdit = async () => {
    if (isStudentLogin) return;
    const record = dialog?.record;
    if (!record) return;
    const changedRows = record.details.filter(d => {
      const id = detailIdOf(d);
      return Boolean(draft[String(id)]) !== sentOf(d) ||
        String(draft[`track_${id}`] ?? "").trim() !== trackOf(d).trim() ||
        dateInputValue(draft[`trackDate_${id}`]) !== dateInputValue(postTrackDateOf(d));
    });
    if (!changedRows.length) { setDialog(null); return; }
    setSaving(true);
    try {
      for (const d of changedRows) {
        const id = detailIdOf(d);
        const payload = {
          Id: id, StudentId: record.sid, ExamId: examIdOf(d),
          BookDetailId: bookIdOf(d),
          PostTrackNo: String(draft[`track_${id}`] ?? "0").trim() || "0",
          PostTrackDate: draft[`trackDate_${id}`] || null,
          CourierCharges: Number(get(d, "courierCharges", "CourierCharges") ?? 0),
          IsBookSend: Boolean(draft[String(id)]),
          IsActive: active(get(d, "isActive", "IsActive")),
        };
        // Preserve API's current PUT verb, updating one book at a time.
        checkResponse(await apiService.put(updateBookStatusUrl, payload));
      }
      setDialog(null); setDraft({});
      notify("success", "Book dispatch details updated successfully");
      await refresh();
    } catch (error) {
      notify("error", error?.response?.data?.failureReason || error?.response?.data?.message || error?.message || "Update failed");
    } finally { setSaving(false); }
  };
  const confirmDelete = async () => {
    if (isStudentLogin) return;
    const d = dialog?.detail, r = dialog?.record;
    if (!d || !r) return;
    setSaving(true);
    try {
      await persistDelete({ studentId: r.sid, studentBookSendId: detailIdOf(d), bookId: bookIdOf(d) });
      notify("success", "Assignment removed"); setDialog(null); await refresh();
    } catch (err) { notify("error", err?.response?.data?.message || err.message || "Delete failed"); }
    finally { setSaving(false); }
  };
  const confirmAdd = async () => {
    if (isStudentLogin) return;
    const payload = {studentId:Number(draft.studentId),examId:Number(draft.examId),bookId:Number(draft.bookId)};
    if (!payload.studentId || !payload.examId || !payload.bookId) return notify("error","Select student, exam and book");
    const existing = records.find(r=>r.sid===payload.studentId)?.details.some(d=>examIdOf(d)===payload.examId && bookIdOf(d)===payload.bookId);
    if (existing) return notify("error","This book is already assigned for the exam");
    setSaving(true);
    try { await persistAdd(payload); notify("success","Book assigned"); setDialog(null); await refresh(); }
    catch(err) { notify("error",err?.response?.data?.message || err.message || "Add failed"); }
    finally { setSaving(false); }
  };
  const selectedRecord = records.find(r=>r.sid===Number(draft.studentId));
  const addExams = selectedRecord?.exams || [];
  const addBooks = [...bookLookup.values()].filter(b => {
    const name=(examLookup.get(Number(draft.examId)) || "").trim().toLowerCase();
    return name && b.examLabel.toLowerCase().split(/[,;|/]+/).map(s=>s.trim()).includes(name);
  });
  const subtle = { fontSize: 12, color: "var(--t3)", marginTop: 4 };
  const chip = (tone = "blue") => ({ display: "inline-flex", alignItems: "center", borderRadius: 20, padding: "4px 9px", fontSize: 11, fontWeight: 650, marginRight: 5, background: tone === "green" ? "#ECFDF5" : tone === "amber" ? "#FFF7ED" : "#EFF6FF", color: tone === "green" ? "#047857" : tone === "amber" ? "#B45309" : "#2563EB" });
  const actionBtn = { border: "1px solid var(--bd, #e2e8f0)", borderRadius: 7, background: "transparent", padding: "6px 9px", cursor: "pointer", fontSize: 12, color: "var(--t2, #334155)" };
  const fieldStyle = { width: "100%", padding: "10px 12px", border: "1px solid var(--bd, #dbe3ed)", borderRadius: 8, background: "var(--card, #fff)", color: "var(--t1, #1e293b)" };
  const labelStyle = { display: "block", fontSize: 12, fontWeight: 650, marginBottom: 6, color: "var(--t2)" };
  return (
    <>
      <div className="ph">
        <div className="ph-title">{title}</div>
        <div className="ph-sub">Manage students' assigned books and dispatch status</div>
      </div>
      <div className="mr" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {[
          [records.length, "Students with Books", "#EFF6FF", "#2563EB"],
          [totalBooks, "Assigned Books", "#ECFDF5", "#059669"],
          [sentBooks, "Sent Books", "#F0FDF4", "#16A34A"],
          [totalBooks - sentBooks, "Pending Books", "#FFF7ED", "#EA580C"],
        ].map(([value, label, bg, color]) => (
          <div className="mc" key={label} style={{ background: bg, flex: "0 0 180px", minWidth: 160 }}>
            <div className="mv" style={{ color }}>{value}</div>
            <div className="ml">{label}</div>
          </div>
        ))}
      </div>
      <div className="tb" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <SB value={search} onChange={value => { setSearch(value); setPage(1); }} placeholder="Search student or book..." />
        {onBack && <button type="button" className="btn" onClick={onBack}>Back</button>}
      </div>
      {loadError && <div style={{ marginBottom: 14, padding: 12, background: "#FEF2F2", color: "#B91C1C", borderRadius: 8 }}>{loadError} — Check StudentBookSend pagination API.</div>}
      {notice && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: "fixed", top: 20, right: 20, zIndex: 99999,
            minWidth: 280, maxWidth: 420, padding: "12px 16px",
            borderRadius: 8,
            backgroundColor: notice.type === "success" ? "#d1e7dd" : "#f8d7da",
            color: notice.type === "success" ? "#0f5132" : "#842029",
            border: notice.type === "success" ? "1px solid #badbcc" : "1px solid #f5c2c7",
            fontSize: 14, fontWeight: 600,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          {notice.type === "success" ? "✓" : "✕"} {notice.text}
        </div>
      )}
      <div className="card">
        <div className="tw">
          <table>
            <thead><tr>
              <th>Tracking No</th><th>Post Track Date</th><th>Student Name</th><th>Registered Exams</th><th>Book Name</th><th>Book Count</th><th>Dispatch Status</th><th>Track Parcel</th><th>Actions</th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={9} style={{ textAlign: "center", padding: 30 }}>Loading assignments...</td></tr> : shown.length === 0 ? <tr><td colSpan={9} style={{ textAlign: "center", padding: 30, color: "var(--t3)" }}>No StudentBookSend records found. Check StudentBookSend pagination response.</td></tr> : shown.map((r, i) => {
                const detail = detailBooks(r);
                const status = r.done === r.total ? "All Sent" : r.done === 0 ? "Pending" : "Partial";
                return <tr key={r.sid}>
                  <td>{r.details.map(d => trackOf(d)).filter(x => x && x !== "0").join(", ") || "—"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{Array.from(new Set(r.details.map(d => dateInputValue(postTrackDateOf(d))).filter(Boolean))).map(dateText).join(", ") || "—"}</td>
                  <td>{r.name}</td>
                  <td>{r.exams.slice(0, 2).map(id => <span key={id} style={chip()}>{examLookup.get(id) || `Exam #${id}`}</span>)}{r.exams.length > 2 && <span style={chip()}>+{r.exams.length - 2}</span>}</td>
                  <td><strong>{detail[0]?._name}</strong>{detail.length > 1 && <button type="button" style={{ ...chip(), border: 0, cursor: "pointer" }} onClick={() => openView(r)} title={detail.slice(1).map(d => d._name).join(", ")}>+{detail.length - 1}</button>}</td>
                  <td>{r.total}</td>
                  <td><span style={chip(r.done === r.total ? "green" : "amber")}>{status}</span><div style={subtle}>{r.done}/{r.total} sent</div></td>
                  <td style={{ minWidth: 115, whiteSpace: "nowrap" }}>
                    {r.details.some(d => trackOf(d).trim() && trackOf(d).trim() !== "0") ? (
                      <a href="https://www.indiapost.gov.in/" target="_blank" rel="noopener noreferrer"
                        title="Open India Post to track the parcel using the tracking number"
                        style={{ ...actionBtn, display: "inline-flex", alignItems: "center", flexWrap: "nowrap", whiteSpace: "nowrap", width: "max-content", textDecoration: "none", color: "#2563EB", fontWeight: 600 }}>
                        Track Parcel
                      </a>
                    ) : <span style={{ color: "var(--t3)", fontSize: 12 }}>—</span>}
                  </td>
                  <td style={{ minWidth: 132, whiteSpace: "nowrap" }}><div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "nowrap", width: "max-content" }}>
                    <button type="button" style={actionBtn} onClick={() => openView(r)} title="View"><I n="eye" s={15} /> </button>
                    {!isStudentLogin && (<button type="button" style={actionBtn} onClick={() => openEdit(r)} title="Edit"><I n="edit" s={15} /> </button>)}
                    {!isStudentLogin && (<button type="button" style={{ ...actionBtn, color: "#DC2626" }} onClick={() => openDelete(r, r.details[0])} title="Delete"><I n="trash" s={15} /> </button>)}
                  </div></td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center", fontSize: 12, color: "var(--t3)" }}>
            <span>Showing <strong style={{ color: "var(--t2)" }}>{filtered.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0}–{Math.min(currentPage * PAGE_SIZE, filtered.length)}</strong> of <strong style={{ color: "var(--t2)" }}>{filtered.length}</strong></span>
          </div>
          <div style={{ display: "flex", gap: 5, alignItems: "center", flexWrap: "wrap" }}>
            <button type="button" disabled={currentPage === 1 || loading}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              style={{ height: 32, minWidth: 66, padding: "0 10px", border: "1px solid #e2e8f0", borderRadius: 6, background: currentPage === 1 ? "#f8fafc" : "#fff", color: currentPage === 1 ? "#cbd5e1" : "#475569", fontSize: 11, cursor: currentPage === 1 ? "not-allowed" : "pointer" }}>
              ‹ Back
            </button>
            {Array.from({ length: maxPage }, (_, i) => i + 1).map(p => (
              <button key={p} type="button" disabled={loading} onClick={() => setPage(p)}
                style={{ height: 32, minWidth: 32, padding: "0 9px", border: p === currentPage ? "1px solid #2563eb" : "1px solid #e2e8f0", borderRadius: 6, background: p === currentPage ? "#2563eb" : "#fff", color: p === currentPage ? "#fff" : "#475569", fontSize: 11, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer" }}>
                {p}
              </button>
            ))}
            <button type="button" disabled={currentPage >= maxPage || loading}
              onClick={() => setPage(p => Math.min(maxPage, p + 1))}
              style={{ height: 32, minWidth: 66, padding: "0 10px", border: "1px solid #e2e8f0", borderRadius: 6, background: currentPage >= maxPage ? "#f8fafc" : "#fff", color: currentPage >= maxPage ? "#cbd5e1" : "#475569", fontSize: 11, cursor: currentPage >= maxPage ? "not-allowed" : "pointer" }}>
              Next ›
            </button>
          </div>
        </div>
      </div>
      {dialog && <div role="presentation" onMouseDown={e => { if (e.target === e.currentTarget && !saving) setDialog(null); }} style={{ position: "fixed", inset: 0, zIndex: 2000, background: "#0f172a88", display: "flex", alignItems: "center", justifyContent: "center", padding: 14 }}>
        <div role="dialog" aria-modal="true" className="card" style={{ width: "100%", maxWidth: 650, maxHeight: "90vh", overflowY: "auto", padding: 22, background: "var(--card, #fff)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ margin: 0 }}>{dialog.type === "view" ? "Student Book Details" : dialog.type === "edit" ? "Update Book Dispatch" : dialog.type === "delete" ? "Remove Book Assignment" : "Assign New Book"}</h3>
            <button type="button" className="btn" disabled={saving} onClick={() => setDialog(null)}>×</button>
          </div>
          {(dialog.type === "view" || dialog.type === "edit") && <>
            <strong>{dialog.record.name} · ID {dialog.record.sid}</strong>
            <p style={subtle}>Each book has its own tracking number, post track date and dispatch status.</p>
            {detailBooks(dialog.record).map((d, i) => <div key={d._detailId || i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", gap: 12, borderBottom: "1px solid var(--bd, #e2e8f0)" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong>{d._name}</strong>
                <div style={{ marginTop: 7 }}>
                  <span style={{ display: "inline-block", background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE", borderRadius: 6, padding: "5px 10px", fontSize: 12, fontWeight: 700 }}>
                    {examLookup.get(d._examId) || `Exam #${d._examId}`}
                  </span>
                </div>
                <div style={{ marginTop: 12, maxWidth: 350 }}>
                  <label style={labelStyle} htmlFor={`post-track-${d._detailId}`}>Tracking No</label>
                  {dialog.type === "edit" ? (
                    <input
                      id={`post-track-${d._detailId}`}
                      type="text"
                      style={fieldStyle}
                      placeholder="Enter tracking number"
                      value={draft[`track_${d._detailId}`] ?? ""}
                      onChange={e => setDraft(prev => ({ ...prev, [`track_${d._detailId}`]: e.target.value }))}
                      maxLength={100}
                    />
                  ) : (
                    <div style={{ fontWeight: 700, color: "var(--t1, #1e293b)" }}>{trackOf(d) || "—"}</div>
                  )}
                </div>
                <div style={{ marginTop: 10, maxWidth: 350 }}>
                  <label style={labelStyle} htmlFor={`post-track-date-${d._detailId}`}>Post Track Date</label>
                  {dialog.type === "edit" ? (
                    <input
                      id={`post-track-date-${d._detailId}`}
                      type="date"
                      style={fieldStyle}
                      value={draft[`trackDate_${d._detailId}`] ?? ""}
                      onChange={e => setDraft(prev => ({ ...prev, [`trackDate_${d._detailId}`]: e.target.value }))}
                    />
                  ) : (
                    <div style={{ fontWeight: 700, color: "var(--t1, #1e293b)" }}>{dateText(postTrackDateOf(d))}</div>
                  )}
                </div>
              </div>
              {dialog.type === "edit" ? <label style={{ display: "flex", gap: 6, alignItems: "center" }}><input type="checkbox" checked={Boolean(draft[String(d._detailId)])} onChange={e => setDraft(prev => ({ ...prev, [String(d._detailId)]: e.target.checked }))} /> Sent</label> : <span style={chip(d._sent ? "green" : "amber")}>{d._sent ? "Sent" : "Pending"}</span>}
            </div>)}
          </>}
          {dialog.type === "delete" && <>
            <p>Remove one assigned book for <strong>{dialog.record.name}</strong>?</p>
            <label style={labelStyle}>Book to remove</label>
            <select style={fieldStyle} value={detailIdOf(dialog.detail)} onChange={e => setDialog(prev => ({ ...prev, detail: prev.record.details.find(d => detailIdOf(d) === Number(e.target.value)) }))}>
              {detailBooks(dialog.record).map(d => <option key={d._detailId} value={d._detailId}>{d._name} — {examLookup.get(d._examId) || `Exam #${d._examId}`}</option>)}
            </select>
            <p style={subtle}>Only this StudentBookSend row is deleted; exam registration and fee data stay unchanged.</p>
          </>}
          {dialog.type === "add" && <>
            <div style={{ marginBottom: 14 }}><label style={labelStyle}>Student</label><select style={fieldStyle} value={draft.studentId} onChange={e => setDraft({ studentId: e.target.value, examId: "", bookId: "" })}><option value="">Select student</option>{records.map(r => <option key={r.sid} value={r.sid}>{r.name} (#{r.sid})</option>)}</select></div>
            <div style={{ marginBottom: 14 }}><label style={labelStyle}>Registered Exam</label><select style={fieldStyle} value={draft.examId} onChange={e => setDraft(prev => ({ ...prev, examId: e.target.value, bookId: "" }))}><option value="">Select exam</option>{addExams.map(id => <option key={id} value={id}>{examLookup.get(id) || `Exam #${id}`}</option>)}</select></div>
            <div style={{ marginBottom: 14 }}><label style={labelStyle}>Book</label><select style={fieldStyle} value={draft.bookId} onChange={e => setDraft(prev => ({ ...prev, bookId: e.target.value }))}><option value="">Select book</option>{addBooks.map(b => <option key={b.id} value={b.id}>{b.name} (#{b.id})</option>)}</select></div>
            <p style={subtle}>A separate StudentBookSend row is created. StudentDetails and exam fees stay unchanged.</p>
          </>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 9, marginTop: 22, flexWrap: "wrap" }}>
            <button type="button" className="btn" disabled={saving} onClick={() => setDialog(null)}>Cancel</button>
            {dialog.type === "view" && !isStudentLogin && <button type="button" className="btn bp" onClick={() => openEdit(dialog.record)}>Edit Status</button>}
            {dialog.type === "edit" && <button type="button" className="btn bp" disabled={saving} onClick={saveEdit}>{saving ? "Saving..." : "Save Changes"}</button>}
            {dialog.type === "delete" && <button type="button" className="btn" style={{ color: "#DC2626" }} disabled={saving} onClick={confirmDelete}>{saving ? "Removing..." : "Remove Assignment"}</button>}
            {dialog.type === "add" && <button type="button" className="btn bp" disabled={saving} onClick={confirmAdd}>{saving ? "Assigning..." : "Assign Book"}</button>}
          </div>
        </div>
      </div>}
    </>
  );
}
